import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import type { ReleaseInput, SuggestedTestCase, ReleaseTarget, BackendChecks } from '../../types';
import { useLanguage } from '../../i18n';
import { useAuth } from '../../auth';
import type { ReleaseFormProps, InputMode } from './types';
import { isPlanningFileValid, isTestCaseFileValid } from './types';
import { useTestCaseAnalyzer, usePlanningAnalyzer } from './hooks';
import { analytics } from '../../analytics';
import { getTeam } from '../../api';

// 임시 저장 데이터 타입
interface DraftData {
  featureName: string;
  releaseDate: string;
  planningIntent: string;
  risks: string;
  qaCore: string;
  qaEdge: string;
  suggestedTestcases: SuggestedTestCase[];
  savedAt: number;
}

const DRAFT_STORAGE_KEY = 'releasegate_form_draft';
const DRAFT_DEBOUNCE_MS = 2000; // 2초 debounce

// 컴포넌트
import LoadingOverlay from './LoadingOverlay';
import DragOverlay from './DragOverlay';
import FileErrorToast from './FileErrorToast';
import BasicInfoSection from './BasicInfoSection';
import PlanningSection from './PlanningSection';
import RisksSection from './RisksSection';
import QAStatusSection from './QAStatusSection';
import SuggestedTestcasesSection from './SuggestedTestcasesSection';
import BackendChecksSection from './BackendChecksSection';

function ReleaseForm({ onSubmit, isLoading, prefillData, isDemo = false, onDemoSubmit, onUnsavedChangesChange }: ReleaseFormProps) {
  const { lang, t } = useLanguage();
  const { quota } = useAuth();
  const [featureName, setFeatureName] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [planningIntent, setPlanningIntent] = useState('');
  const [risks, setRisks] = useState('');
  const [qaCore, setQaCore] = useState('');
  const [qaEdge, setQaEdge] = useState('');
  const [suggestedTestcases, setSuggestedTestcases] = useState<SuggestedTestCase[]>([]);
  const [isGeneratingTestcases, setIsGeneratingTestcases] = useState(false);

  // 평가 대상 (target)
  const [target, setTarget] = useState<ReleaseTarget>('frontend');
  
  // 백엔드 체크 항목 (target=backend 시 사용)
  const [backendChecks, setBackendChecks] = useState<BackendChecks>({});

  // 팀/개인 저장 선택
  const [saveAsPersonal, setSaveAsPersonal] = useState(true); // 기본값: 개인
  
  // Team 플랜 여부
  const isTeamPlan = quota?.plan === 'team';
  
  // 팀 정보 (API에서 가져옴)
  const [teamInfo, setTeamInfo] = useState<{ team_id: number; team_name?: string } | null>(null);
  
  // 팀 정보 로드 (Team 플랜이면 시도, 팀이 없으면 null)
  useEffect(() => {
    if (!isTeamPlan || isDemo) {
      setTeamInfo(null);
      setSaveAsPersonal(true);
      return;
    }
    // 팀 이름 가져오기
    getTeam()
      .then((team) => {
        if (team) {
          setTeamInfo({ team_id: team.team.id, team_name: team.team.name });
          setSaveAsPersonal(false); // 팀이 있으면 기본값: 팀
        } else {
          setTeamInfo(null);
          setSaveAsPersonal(true);
        }
      })
      .catch(() => {
        setTeamInfo(null);
        setSaveAsPersonal(true);
      });
  }, [isTeamPlan, isDemo]);

  // 다시 평가하기 모드 상태
  const [isReEvaluate, setIsReEvaluate] = useState(false);
  
  // 사용자가 직접 수정했는지 추적 (prefillData 로드와 구분)
  const [userHasModified, setUserHasModified] = useState(false);
  
  // 임시 저장 관련 상태
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [savedDraft, setSavedDraft] = useState<DraftData | null>(null);
  const [draftSaveIndicator, setDraftSaveIndicator] = useState(false);
  const draftSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // prefillData가 변경되면 폼에 채우기
  useEffect(() => {
    if (prefillData) {
      setFeatureName(prefillData.feature_name);
      setReleaseDate(prefillData.release_date);
      setPlanningIntent(prefillData.planning_intent.join('\n'));
      setRisks(prefillData.risks.join('\n'));
      setIsReEvaluate(prefillData.is_re_evaluate || false);
      
      // QA 상태에서 값 추출
      const qaValues = Object.values(prefillData.qa_status);
      if (qaValues.length >= 1) setQaCore(qaValues[0]);
      if (qaValues.length >= 2) setQaEdge(qaValues[1]);
      
      // 테스트케이스 복원
      if (prefillData.suggested_testcases && prefillData.suggested_testcases.length > 0) {
        setSuggestedTestcases(prefillData.suggested_testcases);
      }
    }
  }, [prefillData]);

  // 페이지 로드 시 임시 저장 데이터 확인
  useEffect(() => {
    if (isDemo || prefillData) return; // 데모 모드나 prefill 데이터가 있으면 스킵
    
    try {
      const draftJson = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (draftJson) {
        const draft: DraftData = JSON.parse(draftJson);
        // 24시간 이내 데이터만 유효
        const isValid = Date.now() - draft.savedAt < 24 * 60 * 60 * 1000;
        // 데이터가 실제로 있는지 확인
        const hasContent =
          draft.featureName ||
          draft.planningIntent ||
          draft.risks ||
          draft.suggestedTestcases.length > 0;
        
        if (isValid && hasContent) {
          setSavedDraft(draft);
          setShowDraftModal(true);
        } else if (!isValid) {
          // 만료된 데이터 삭제
          localStorage.removeItem(DRAFT_STORAGE_KEY);
        }
      }
    } catch (e) {
      console.error('Failed to load draft:', e);
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    }
  }, [isDemo, prefillData]);

  // 임시 저장 함수 (debounce)
  const saveDraft = useCallback(() => {
    if (isDemo) return;
    
    // 기존 타이머 취소
    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current);
    }
    
    draftSaveTimeoutRef.current = setTimeout(() => {
      const hasContent =
        featureName ||
        planningIntent ||
        risks ||
        suggestedTestcases.length > 0;
      
      if (hasContent) {
        const draft: DraftData = {
          featureName,
          releaseDate,
          planningIntent,
          risks,
          qaCore,
          qaEdge,
          suggestedTestcases,
          savedAt: Date.now(),
        };
        
        try {
          localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
          // 저장 표시기 잠시 보여주기
          setDraftSaveIndicator(true);
          setTimeout(() => setDraftSaveIndicator(false), 2000);
        } catch (e) {
          console.error('Failed to save draft:', e);
        }
      }
    }, DRAFT_DEBOUNCE_MS);
  }, [isDemo, featureName, releaseDate, planningIntent, risks, qaCore, qaEdge, suggestedTestcases]);

  // 폼 데이터 변경 시 자동 저장
  useEffect(() => {
    if (userHasModified && !isDemo) {
      saveDraft();
    }
  }, [featureName, releaseDate, planningIntent, risks, qaCore, qaEdge, suggestedTestcases, userHasModified, isDemo, saveDraft]);

  // 임시 저장 데이터 복원
  const restoreDraft = useCallback(() => {
    if (savedDraft) {
      setFeatureName(savedDraft.featureName);
      setReleaseDate(savedDraft.releaseDate);
      setPlanningIntent(savedDraft.planningIntent);
      setRisks(savedDraft.risks);
      setQaCore(savedDraft.qaCore);
      setQaEdge(savedDraft.qaEdge);
      setSuggestedTestcases(savedDraft.suggestedTestcases);
      setUserHasModified(true);
    }
    setShowDraftModal(false);
    setSavedDraft(null);
  }, [savedDraft]);

  // 임시 저장 데이터 삭제 (새로 시작)
  const discardDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setShowDraftModal(false);
    setSavedDraft(null);
  }, []);

  // 임시 저장 데이터 삭제 함수 (폼 제출/초기화 시 사용)
  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current);
    }
  }, []);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current);
      }
    };
  }, []);

  // 파일 업로드 상태
  const [planningInputMode, setPlanningInputMode] = useState<InputMode>('manual');
  const [qaInputMode, setQaInputMode] = useState<InputMode>('manual');
  const [planningFiles, setPlanningFiles] = useState<File[]>([]);
  const [testCaseFiles, setTestCaseFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  const formRef = useRef<HTMLFormElement>(null);

  // 커스텀 훅 사용
  const {
    testCaseAnalysis,
    isAnalyzing: isTestCaseAnalyzing,
    analyzeTestCaseFiles,
    clearAnalysis: clearTestCaseAnalysis,
  } = useTestCaseAnalyzer();

  const {
    screenshotAnalyses,
    extractedPlanningItems,
    isAnalyzing: isPlanningAnalyzing,
    analyzePlanningFiles,
    clearAnalysis: clearPlanningAnalysis,
  } = usePlanningAnalyzer();

  const isAnalyzing = isTestCaseAnalyzing || isPlanningAnalyzing;

  // 변경사항 감지 (사용자가 직접 수정한 경우에만)
  const hasUnsavedChanges = useMemo(() => {
    // 데모 모드에서는 감지하지 않음
    if (isDemo) return false;
    
    // 파일 첨부 여부 (파일은 prefillData로 로드되지 않으므로 항상 사용자 액션)
    const hasFiles = planningFiles.length > 0 || testCaseFiles.length > 0;
    
    // 파일이 첨부되었거나 사용자가 직접 텍스트를 수정한 경우에만 true
    return hasFiles || userHasModified;
  }, [isDemo, planningFiles, testCaseFiles, userHasModified]);

  // 변경사항 상태를 부모에게 전달
  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  // 브라우저 beforeunload 이벤트 제거됨 - 임시 저장 기능이 있으므로 불필요

  // Free 플랜 여부 확인 (파일 첨부 기능 제한용)
  const isFreePlan = quota?.plan === 'free';

  // 입력 제한 검증
  const inputValidation = useMemo(() => {
    const limits = quota?.limits ?? { max_test_cases: 10, max_planning_chars: 3000 };
    const planningLength = planningIntent.length;
    const testCaseCount = testCaseAnalysis?.total ?? 0;
    
    const planningExceeded = planningLength > limits.max_planning_chars;
    const testCaseExceeded = testCaseCount > limits.max_test_cases;
    
    return {
      planningLength,
      planningLimit: limits.max_planning_chars,
      planningExceeded,
      testCaseCount,
      testCaseLimit: limits.max_test_cases,
      testCaseExceeded,
      hasError: planningExceeded || testCaseExceeded,
    };
  }, [planningIntent, testCaseAnalysis, quota]);

  // 테스트 케이스 분석 결과를 QA 상태에 반영
  useEffect(() => {
    if (testCaseAnalysis) {
      setQaCore(testCaseAnalysis.coreStatus);
      setQaEdge(testCaseAnalysis.edgeStatus);
    }
  }, [testCaseAnalysis]);

  // 기획 의도 분석 결과 반영
  useEffect(() => {
    if (extractedPlanningItems.length > 0) {
      setPlanningIntent(extractedPlanningItems.join('\n'));
    }
  }, [extractedPlanningItems]);

  // 에러 알림 표시 (3초 후 자동 사라짐)
  const showFileError = useCallback((message: string) => {
    setFileError(message);
    setTimeout(() => setFileError(null), 4000);
  }, []);

  // 전체 폼 드래그 이벤트 핸들러
  const handleFormDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleFormDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleFormDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // 전체 폼에 드롭 시 파일 타입에 따라 자동 분류
  const handleFormDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounterRef.current = 0;

      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;

      const planningFilesToAdd: File[] = [];
      const testCaseFilesToAdd: File[] = [];
      const unsupportedFiles: string[] = [];

      files.forEach((file) => {
        if (isTestCaseFileValid(file)) {
          testCaseFilesToAdd.push(file);
        } else if (isPlanningFileValid(file)) {
          planningFilesToAdd.push(file);
        } else {
          unsupportedFiles.push(file.name);
        }
      });

      // 지원하지 않는 파일 알림
      if (unsupportedFiles.length > 0) {
        showFileError(
          lang === 'ko'
            ? `⚠️ 지원하지 않는 파일: ${unsupportedFiles.join(', ')}`
            : `⚠️ Unsupported files: ${unsupportedFiles.join(', ')}`
        );
      }

      // 기획 파일 처리
      if (planningFilesToAdd.length > 0) {
        setPlanningInputMode('file');
        setPlanningFiles((prev) => [...prev, ...planningFilesToAdd]);
        analyzePlanningFiles([...planningFiles, ...planningFilesToAdd]);
      }

      // 테스트 케이스 파일 처리 (플랜별 제한 적용)
      if (testCaseFilesToAdd.length > 0) {
        const testCaseFileLimit = quota?.limits?.max_testcase_files ?? 1;
        const newTestCaseFiles = [...testCaseFiles, ...testCaseFilesToAdd];
        
        if (newTestCaseFiles.length > testCaseFileLimit) {
          showFileError(
            lang === 'ko'
              ? `⚠️ 테스트 케이스 파일은 최대 ${testCaseFileLimit}개까지 첨부 가능합니다. (현재 플랜 기준)`
              : `⚠️ You can attach up to ${testCaseFileLimit} test case files. (Based on your plan)`
          );
          const limitedFiles = newTestCaseFiles.slice(0, testCaseFileLimit);
          setQaInputMode('file');
          setTestCaseFiles(limitedFiles);
          analyzeTestCaseFiles(limitedFiles);
        } else {
          setQaInputMode('file');
          setTestCaseFiles(newTestCaseFiles);
          analyzeTestCaseFiles(newTestCaseFiles);
        }
        
        // GA4: 파일 업로드 이벤트
        analytics.fileUpload('testcase', testCaseFilesToAdd.length);
      }
      
      // GA4: 기획 파일 업로드 이벤트
      if (planningFilesToAdd.length > 0) {
        analytics.fileUpload('planning', planningFilesToAdd.length);
      }
    },
    [lang, planningFiles, testCaseFiles, analyzePlanningFiles, analyzeTestCaseFiles, showFileError, quota]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 디버그: 파일 상태 확인
    console.log('🔍 handleSubmit - File state:', {
      planningInputMode,
      planningFilesCount: planningFiles.length,
      planningFileNames: planningFiles.map(f => f.name),
      testCaseFilesCount: testCaseFiles.length,
      testCaseFileNames: testCaseFiles.map(f => f.name),
    });

    // 데모 모드에서는 onDemoSubmit 호출
    if (isDemo && onDemoSubmit) {
      onDemoSubmit();
      return;
    }

    // QA 상태 키는 언어에 따라 다르게 설정
    const coreKey = lang === 'ko' ? '핵심 시나리오' : 'Core Scenario';
    const edgeKey = lang === 'ko' ? '엣지 케이스' : 'Edge Cases';

    const input: ReleaseInput = {
      feature_name: featureName,
      release_date: releaseDate,
      target: target,
      planning_intent: planningIntent.split('\n').filter((line) => line.trim()),
      risks: risks.split('\n').filter((line) => line.trim()),
      qa_status: {
        [coreKey]: qaCore || t.qaValue.notEntered,
        [edgeKey]: qaEdge || t.qaValue.notEntered,
      },
      // 백엔드 체크 항목 (target이 backend 또는 fullstack인 경우)
      backend_checks: (target === 'backend' || target === 'fullstack') ? backendChecks : undefined,
      lang: lang,
      suggested_testcases: suggestedTestcases.length > 0 ? suggestedTestcases : undefined,
      // 팀 소속 사용자의 개인 저장 선택
      save_as_personal: teamInfo ? saveAsPersonal : undefined,
    };

    // 테스트케이스 파일이 있으면 base64로 인코딩하여 추가
    if (testCaseFiles.length > 0) {
      const encodedTestCaseFiles = [];
      for (const file of testCaseFiles) {
        try {
          const fileContent = await file.arrayBuffer();
          const base64Content = btoa(
            new Uint8Array(fileContent).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              ''
            )
          );
          encodedTestCaseFiles.push({
            filename: file.name,
            content: base64Content,
            content_type: file.type || undefined,
          });
        } catch (error) {
          console.error('Failed to encode testcase file:', file.name, error);
          // 개별 파일 인코딩 실패해도 계속 진행
        }
      }
      if (encodedTestCaseFiles.length > 0) {
        input.testcase_files = encodedTestCaseFiles;
        // 기존 호환성: 첫 번째 파일은 testcase_file에도 저장
        input.testcase_file = encodedTestCaseFiles[0];
        console.log('📎 Testcase files to send:', encodedTestCaseFiles.length, encodedTestCaseFiles.map(f => f.filename));
      }
    }

    // 기획 파일이 있으면 base64로 인코딩하여 추가
    if (planningFiles.length > 0) {
      const encodedPlanningFiles = [];
      for (const file of planningFiles) {
        try {
          const fileContent = await file.arrayBuffer();
          const base64Content = btoa(
            new Uint8Array(fileContent).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              ''
            )
          );
          encodedPlanningFiles.push({
            filename: file.name,
            content: base64Content,
            content_type: file.type || undefined,
          });
        } catch (error) {
          console.error('Failed to encode planning file:', file.name, error);
          // 개별 파일 인코딩 실패해도 계속 진행
        }
      }
      if (encodedPlanningFiles.length > 0) {
        input.planning_files = encodedPlanningFiles;
        console.log('📎 Planning files to send:', encodedPlanningFiles.length, encodedPlanningFiles.map(f => f.filename));
      }
    } else {
      console.log('📎 No planning files to send (planningFiles.length = 0)');
    }

    // 디버그: 최종 input 확인
    console.log('📤 Final input:', {
      ...input,
      testcase_file: input.testcase_file ? { filename: input.testcase_file.filename, contentLength: input.testcase_file.content.length } : undefined,
      testcase_files: input.testcase_files?.map(f => ({ filename: f.filename, contentLength: f.content.length })),
      planning_files: input.planning_files?.map(f => ({ filename: f.filename, contentLength: f.content.length })),
    });

    // GA4: 평가 요청 이벤트
    analytics.evaluateSubmit(testCaseFiles.length > 0, planningFiles.length > 0);

    // 폼 제출 시 임시 저장 데이터 삭제
    clearDraft();

    onSubmit(input);
  };

  // GA4: 폼 작성 시작 추적 (첫 입력 시) + 사용자 수정 상태 추적
  const handleFormStart = useCallback(() => {
    if (!featureName && !releaseDate && !planningIntent && !risks) {
      analytics.formStart();
    }
    // 사용자가 직접 입력했음을 표시
    setUserHasModified(true);
  }, [featureName, releaseDate, planningIntent, risks]);

  // 모든 폼 데이터 초기화
  const handleClearAll = useCallback(() => {
    // 기본 정보 초기화
    setFeatureName('');
    setReleaseDate('');
    setTarget('frontend');
    
    // 백엔드 체크 초기화
    setBackendChecks({});
    
    // 기획 의도 초기화
    setPlanningIntent('');
    setPlanningInputMode('manual');
    setPlanningFiles([]);
    clearPlanningAnalysis();
    
    // 리스크 초기화
    setRisks('');
    
    // QA 상태 초기화
    setQaCore('');
    setQaEdge('');
    setQaInputMode('manual');
    setTestCaseFiles([]);
    clearTestCaseAnalysis();
    
    // AI 제안 테스트 케이스 초기화
    setSuggestedTestcases([]);
    
    // 수정 상태 초기화
    setUserHasModified(false);
    
    // 임시 저장 데이터도 삭제
    clearDraft();
  }, [clearPlanningAnalysis, clearTestCaseAnalysis, clearDraft]);

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      onDragEnter={isDemo ? undefined : handleFormDragEnter}
      onDragLeave={isDemo ? undefined : handleFormDragLeave}
      onDragOver={isDemo ? undefined : handleFormDragOver}
      onDrop={isDemo ? undefined : handleFormDrop}
      className="space-y-6 relative"
      data-testid="release-form"
    >
      {/* 오버레이 & 토스트 */}
      <LoadingOverlay isLoading={isLoading} />
      {/* 분석(파일/AI 추출) 중 전체 화면 디밍 */}
      {isAnalyzing && (
        <div className="fixed inset-0 z-[55] bg-black/60 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 px-4">
            <div className="w-12 h-12 border-4 border-status-warning/30 border-t-status-warning rounded-full animate-spin"></div>
            <div className="text-terminal-text text-base font-mono">
              {t.form.analyzing}
              <span className="cursor-blink"></span>
            </div>
            <div className="text-terminal-muted text-sm text-center">
              {lang === 'ko' ? '파일/내용을 분석 중입니다...' : 'Analyzing content/files...'}
            </div>
          </div>
        </div>
      )}
      <DragOverlay isDragging={isDragging} />
      <FileErrorToast error={fileError} onClose={() => setFileError(null)} />

      {/* 임시 저장 복원 모달 */}
      {showDraftModal && savedDraft && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#1a1a2e] border border-terminal-border rounded-lg p-5 w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">📝</span>
              <div>
                <h3 className="text-terminal-text font-medium">
                  {t.form.draftRestoreTitle || '임시 저장된 내용이 있습니다'}
                </h3>
                <p className="text-terminal-dim text-xs mt-1">
                  {(t.form.draftRestoreTime || '{time}에 저장됨').replace(
                    '{time}',
                    new Date(savedDraft.savedAt).toLocaleString(lang === 'ko' ? 'ko-KR' : 'en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  )}
                </p>
              </div>
            </div>
            
            {/* 미리보기 */}
            <div className="mb-4 p-3 bg-terminal-bg rounded border border-terminal-border text-xs space-y-1">
              {savedDraft.featureName && (
                <p className="text-terminal-text truncate">
                  <span className="text-terminal-dim">기능명:</span> {savedDraft.featureName}
                </p>
              )}
              {savedDraft.planningIntent && (
                <p className="text-terminal-muted truncate">
                  <span className="text-terminal-dim">기획:</span> {savedDraft.planningIntent.split('\n')[0]}...
                </p>
              )}
              {savedDraft.suggestedTestcases.length > 0 && (
                <p className="text-terminal-muted">
                  <span className="text-terminal-dim">테스트케이스:</span> {savedDraft.suggestedTestcases.length}개
                </p>
              )}
            </div>
            
            <p className="text-terminal-muted text-sm mb-4">
              {t.form.draftRestoreMessage || '이전에 작성하던 내용을 불러올까요?'}
            </p>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={discardDraft}
                className="flex-1 px-4 py-2.5 text-sm border border-terminal-border text-terminal-muted rounded hover:text-terminal-text hover:border-terminal-muted transition-colors"
              >
                {t.form.draftDiscard || '새로 시작'}
              </button>
              <button
                type="button"
                onClick={restoreDraft}
                className="flex-1 px-4 py-2.5 text-sm bg-status-go/20 border border-status-go text-status-go rounded hover:bg-status-go/30 transition-colors font-medium"
              >
                {t.form.draftRestore || '불러오기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 자동 저장 표시기 */}
      {draftSaveIndicator && !isDemo && (
        <div className="fixed bottom-4 right-4 bg-[#1a1a2e] border border-terminal-border rounded-lg px-4 py-2 shadow-lg z-40 flex items-center gap-2 animate-fade-in">
          <span className="text-status-go text-sm">✓</span>
          <span className="text-terminal-muted text-xs">
            {t.form.draftSaved || '임시 저장됨'}
          </span>
        </div>
      )}

      {/* 데모 모드 워터마크 - 반복 패턴으로 전체 커버 */}
      {isDemo && (
        <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='200'%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial Black, sans-serif' font-size='36' font-weight='900' fill='%23000' transform='rotate(-25 200 100)'%3EReleaseGate%3C/text%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat',
              backgroundSize: '400px 200px',
            }}
          />
          <div 
            className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='200'%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial Black, sans-serif' font-size='36' font-weight='900' fill='%23fff' transform='rotate(-25 200 100)'%3EReleaseGate%3C/text%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat',
              backgroundSize: '400px 200px',
            }}
          />
        </div>
      )}

      {/* 기본 정보 */}
      <BasicInfoSection
        featureName={featureName}
        setFeatureName={(val) => { handleFormStart(); setFeatureName(val); }}
        releaseDate={releaseDate}
        setReleaseDate={(val) => { handleFormStart(); setReleaseDate(val); }}
        isDemo={isDemo}
        isReEvaluate={isReEvaluate}
        isTeamPlan={isTeamPlan}
        teamInfo={teamInfo}
        saveAsPersonal={saveAsPersonal}
        setSaveAsPersonal={setSaveAsPersonal}
        target={target}
        setTarget={(val) => { setUserHasModified(true); setTarget(val); }}
      />

      {/* 백엔드 체크리스트 (target이 backend 또는 fullstack인 경우) */}
      {(target === 'backend' || target === 'fullstack') && (
        <BackendChecksSection
          backendChecks={backendChecks}
          setBackendChecks={(checks) => { setUserHasModified(true); setBackendChecks(checks); }}
          isDemo={isDemo}
        />
      )}

      {/* 기획 의도 */}
      <PlanningSection
        planningIntent={planningIntent}
        setPlanningIntent={(val) => { setUserHasModified(true); setPlanningIntent(val); }}
        planningInputMode={planningInputMode}
        setPlanningInputMode={setPlanningInputMode}
        planningFiles={planningFiles}
        setPlanningFiles={setPlanningFiles}
        screenshotAnalyses={screenshotAnalyses}
        extractedPlanningItems={extractedPlanningItems}
        isAnalyzing={isPlanningAnalyzing}
        analyzePlanningFiles={analyzePlanningFiles}
        clearPlanningAnalysis={clearPlanningAnalysis}
        showFileError={showFileError}
        charCount={inputValidation.planningLength}
        charLimit={inputValidation.planningLimit}
        isDemo={isDemo}
        featureName={featureName}
        isFreePlan={isFreePlan}
      />

      {/* 리스크 */}
      <RisksSection 
        risks={risks} 
        setRisks={(val) => { setUserHasModified(true); setRisks(val); }} 
        isDemo={isDemo} 
      />

      {/* QA 상태 */}
      <QAStatusSection
        qaCore={qaCore}
        setQaCore={(val) => { setUserHasModified(true); setQaCore(val); }}
        qaEdge={qaEdge}
        setQaEdge={(val) => { setUserHasModified(true); setQaEdge(val); }}
        qaInputMode={qaInputMode}
        setQaInputMode={setQaInputMode}
        testCaseFiles={testCaseFiles}
        setTestCaseFiles={setTestCaseFiles}
        testCaseAnalysis={testCaseAnalysis}
        isAnalyzing={isTestCaseAnalyzing}
        analyzeTestCaseFiles={analyzeTestCaseFiles}
        clearTestCaseAnalysis={clearTestCaseAnalysis}
        showFileError={showFileError}
        testCaseLimit={inputValidation.testCaseLimit}
        testCaseFileLimit={quota?.limits?.max_testcase_files ?? 1}
        isDemo={isDemo}
        featureName={featureName}
        isFreePlan={isFreePlan}
      />

      {/* AI 제안 테스트 케이스 */}
      <SuggestedTestcasesSection
        featureName={featureName}
        planningIntent={planningIntent}
        risks={risks}
        suggestedTestcases={suggestedTestcases}
        setSuggestedTestcases={setSuggestedTestcases}
        isDemo={isDemo}
        qaCore={qaCore}
        qaEdge={qaEdge}
        onGeneratingChange={setIsGeneratingTestcases}
      />

      {/* 입력 제한 경고 */}
      {inputValidation.hasError && (
        <div className="p-3 bg-status-danger/10 border border-status-danger/30 rounded text-sm text-status-danger">
          <p className="font-medium mb-1">
            {lang === 'ko' ? '⚠️ 입력 제한 초과' : '⚠️ Input Limit Exceeded'}
          </p>
          <ul className="list-disc list-inside text-xs space-y-1">
            {inputValidation.planningExceeded && (
              <li>
                {lang === 'ko'
                  ? `Planning: ${inputValidation.planningLength.toLocaleString()}자 / ${inputValidation.planningLimit.toLocaleString()}자 (초과)`
                  : `Planning: ${inputValidation.planningLength.toLocaleString()} / ${inputValidation.planningLimit.toLocaleString()} chars (exceeded)`}
              </li>
            )}
            {inputValidation.testCaseExceeded && (
              <li>
                {lang === 'ko'
                  ? `테스트케이스: ${inputValidation.testCaseCount}개 / ${inputValidation.testCaseLimit}개 (초과)`
                  : `Test cases: ${inputValidation.testCaseCount} / ${inputValidation.testCaseLimit} (exceeded)`}
              </li>
            )}
          </ul>
          <p className="text-xs mt-2 text-terminal-muted">
            {lang === 'ko'
              ? '플랜을 업그레이드하면 더 많은 입력이 가능합니다.'
              : 'Upgrade your plan for higher limits.'}
          </p>
        </div>
      )}

      {/* 입력 상태 표시 (에러 아닐 때) */}
      {!inputValidation.hasError && (planningIntent.length > 0 || inputValidation.testCaseCount > 0) && (
        <div className="flex items-center gap-4 text-xs text-terminal-muted">
          <span>
            📝 Planning: {inputValidation.planningLength.toLocaleString()} / {inputValidation.planningLimit.toLocaleString()}
            {lang === 'ko' ? '자' : ' chars'}
          </span>
          {inputValidation.testCaseCount > 0 && (
            <span>
              📋 {lang === 'ko' ? '테스트케이스' : 'Test cases'}: {inputValidation.testCaseCount} / {inputValidation.testCaseLimit}
            </span>
          )}
        </div>
      )}

      {/* 제출 및 삭제 버튼 */}
      <div className="flex gap-3">
        {/* 모두 삭제 버튼 */}
        {!isDemo && (
          <button
            type="button"
            onClick={handleClearAll}
            disabled={isLoading || isAnalyzing || isGeneratingTestcases}
            className={`
              px-4 py-3 rounded font-medium text-sm
              border transition-all
              ${
                isLoading || isAnalyzing || isGeneratingTestcases
                  ? 'bg-terminal-input border-terminal-border text-terminal-dim cursor-not-allowed'
                  : 'bg-transparent border-terminal-border text-terminal-muted hover:border-status-danger hover:text-status-danger'
              }
            `}
          >
            {t.form.clearAll}
          </button>
        )}
        
        {/* 제출 버튼 */}
        <button
          type="submit"
          disabled={isLoading || isAnalyzing || isGeneratingTestcases || (!isDemo && (!featureName || !releaseDate)) || inputValidation.hasError}
          className={`
            flex-1 py-3 rounded font-medium text-sm
            border transition-all
            ${
              isLoading || isAnalyzing || isGeneratingTestcases || (!isDemo && (!featureName || !releaseDate)) || inputValidation.hasError
                ? 'bg-terminal-input border-terminal-border text-terminal-dim cursor-not-allowed'
                : isDemo
                  ? 'bg-[var(--neo-mint)]/20 border-[var(--neo-mint)] text-[var(--neo-mint)] hover:bg-[var(--neo-mint)]/30'
                  : 'bg-status-go/10 border-status-go text-status-go hover:bg-status-go/20'
            }
          `}
          data-testid="submit-button"
        >
          {isLoading ? (
            <span>
              {t.form.evaluating}
              <span className="cursor-blink"></span>
            </span>
          ) : isDemo ? (
            lang === 'ko' ? '> 데모 결과 보기' : '> View Demo Result'
          ) : (
            t.form.runEvaluate
          )}
        </button>
      </div>

      {/* 데모 모드 안내 */}
      {isDemo && (
        <div className="flex items-center justify-center gap-2 text-xs text-terminal-muted">
          <span className="w-2 h-2 bg-[var(--neo-mint)] rounded-full animate-pulse"></span>
          {lang === 'ko' 
            ? '로그인하면 직접 입력하여 AI 평가를 받을 수 있습니다' 
            : 'Login to input your own data and get AI evaluation'}
        </div>
      )}

    </form>
  );
}

export default ReleaseForm;

